import { useCallback, useSyncExternalStore, useState } from "react";
import type { CategoryFilter, ViewMode } from "../shared/types";
import { track } from "../lib/analytics";

export function createBoolStore(key: string, defaultValue = false) {
  const listeners = new Set<() => void>();
  const stored = typeof window !== "undefined" ? localStorage.getItem(key) : null;
  let value = stored !== null ? stored === "true" : defaultValue;

  function subscribe(listener: () => void) {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  }
  function get() { return value; }
  function set(next: boolean) {
    value = next;
    localStorage.setItem(key, String(next));
    for (const l of listeners) l();
  }
  function toggle() {
    set(!value);
    return value;
  }
  return { subscribe, get, set, toggle } as const;
}

const pomodoroStore = createBoolStore("palnia_show_pomodoro");
const twentyStore = createBoolStore("palnia_show_twenty");
const stopwatchStore = createBoolStore("palnia_show_stopwatch");
const advancedModeStore = createBoolStore("palnia_advanced_mode");
const googleCalendarUiStore = createBoolStore("palnia_google_calendar_ui");
const settingsCollapsedStore = createBoolStore("palnia_settings_collapsed");
const bottomBarStore = createBoolStore("palnia_desktop_bottom_bar");
export const calendarReadOnlyStore = createBoolStore("palnia_calendar_readonly");
export const showHolidaysStore = createBoolStore("palnia_show_holidays", true);

// Shared store for hiddenSections (syncs across multiple useAppState instances)
const hiddenSectionsListeners = new Set<() => void>();
let hiddenSectionsValue: ReadonlySet<ViewMode> = (() => {
  if (typeof window === "undefined") return new Set<ViewMode>();
  try {
    const raw = localStorage.getItem("palnia_hidden_sections");
    if (raw) return new Set<ViewMode>(JSON.parse(raw));
  } catch { /* ignore */ }
  return new Set<ViewMode>();
})();

function subscribeHiddenSections(listener: () => void) {
  hiddenSectionsListeners.add(listener);
  return () => { hiddenSectionsListeners.delete(listener); };
}
function getHiddenSections() { return hiddenSectionsValue; }
function setHiddenSections(next: ReadonlySet<ViewMode>) {
  hiddenSectionsValue = next;
  localStorage.setItem("palnia_hidden_sections", JSON.stringify([...next]));
  for (const l of hiddenSectionsListeners) l();
}

function getSidebarPinnedState(): boolean {
  if (typeof window === "undefined") return false;
  return localStorage.getItem("palnia_sidebar_expanded") === "true";
}

export function useAppState() {
  const [activeFilter, setActiveFilter] = useState<CategoryFilter>("all");
  const [hideHidden, setHideHidden] = useState(false);

  const showPomodoro = useSyncExternalStore(pomodoroStore.subscribe, pomodoroStore.get);
  const showTwenty = useSyncExternalStore(twentyStore.subscribe, twentyStore.get);
  const showStopwatch = useSyncExternalStore(stopwatchStore.subscribe, stopwatchStore.get);
  const advancedMode = useSyncExternalStore(advancedModeStore.subscribe, advancedModeStore.get);
  const googleCalendarUi = useSyncExternalStore(googleCalendarUiStore.subscribe, googleCalendarUiStore.get);
  const settingsCollapsed = useSyncExternalStore(settingsCollapsedStore.subscribe, settingsCollapsedStore.get);
  const desktopBottomBar = useSyncExternalStore(bottomBarStore.subscribe, bottomBarStore.get);
  const calendarReadOnly = useSyncExternalStore(calendarReadOnlyStore.subscribe, calendarReadOnlyStore.get);
  const hiddenSections = useSyncExternalStore(subscribeHiddenSections, getHiddenSections);

  const [sidebarPinned, setSidebarPinned] = useState<boolean>(
    getSidebarPinnedState,
  );

  const togglePomodoro = useCallback(() => {
    pomodoroStore.toggle();
    track("timer_panel_toggled", { type: "pomodoro", visible: pomodoroStore.get() });
  }, []);

  const toggleTwenty = useCallback(() => {
    twentyStore.toggle();
    track("timer_panel_toggled", { type: "twenty", visible: twentyStore.get() });
  }, []);

  const toggleStopwatch = useCallback(() => {
    stopwatchStore.toggle();
    track("timer_panel_toggled", { type: "stopwatch", visible: stopwatchStore.get() });
  }, []);

  const toggleAdvancedMode = useCallback(() => {
    advancedModeStore.toggle();
    track("advanced_mode_toggled", { enabled: advancedModeStore.get() });
  }, []);

  const toggleGoogleCalendarUi = useCallback(() => {
    googleCalendarUiStore.toggle();
    track("google_calendar_ui_toggled", { enabled: googleCalendarUiStore.get() });
  }, []);

  const toggleSettingsCollapsed = useCallback(() => {
    settingsCollapsedStore.toggle();
    // Reset persisted open sections so the new default takes effect next open
    localStorage.removeItem("palnia_settings_open_sections");
  }, []);

  const toggleDesktopBottomBar = useCallback(() => {
    bottomBarStore.toggle();
    track("desktop_bottom_bar_toggled", { enabled: bottomBarStore.get() });
  }, []);

  const toggleSection = useCallback((mode: ViewMode) => {
    const current = getHiddenSections();
    const next = new Set(current);
    if (next.has(mode)) {
      next.delete(mode);
    } else {
      next.add(mode);
    }
    setHiddenSections(next);
    track("section_visibility_toggled", { section: mode, hidden: next.has(mode) });
  }, []);

  const toggleCalendarReadOnly = useCallback(() => {
    calendarReadOnlyStore.toggle();
    track("calendar_readonly_toggled", { enabled: calendarReadOnlyStore.get() });
  }, []);

  const showHolidays = useSyncExternalStore(showHolidaysStore.subscribe, showHolidaysStore.get);
  const toggleHolidays = useCallback(() => {
    showHolidaysStore.toggle();
  }, []);

  return {
    activeFilter,
    setActiveFilter,
    hideHidden,
    setHideHidden,
    showPomodoro,
    togglePomodoro,
    showTwenty,
    toggleTwenty,
    showStopwatch,
    toggleStopwatch,
    advancedMode,
    toggleAdvancedMode,
    googleCalendarUi,
    toggleGoogleCalendarUi,
    settingsCollapsed,
    toggleSettingsCollapsed,
    sidebarPinned,
    setSidebarPinned,
    desktopBottomBar,
    toggleDesktopBottomBar,
    hiddenSections,
    toggleSection,
    calendarReadOnly,
    toggleCalendarReadOnly,
    showHolidays,
    toggleHolidays,
  };
}
